import { Heart } from 'lucide-react'
import { useAuth } from 'react-oidc-context'
import { FavoriteHeartButton } from '@/components/favorites/favorite-heart-button'
import { useFavoriteStats } from '@/features/favorites/use-favorites'
import { useMyFollowings } from '@/features/doer/use-doer'

interface ProjectDetailFavoriteSlotProps {
  projectId: string
}

/**
 * Project Detail header slot — heart toggle + aggregate favorite count. Phase 10 visibility rules:
 *
 * <ul>
 *   <li>Count visible to all viewers (anonymous + signed-in).
 *   <li>Heart hidden for anonymous viewers (clicking would just bounce to /login).
 *   <li>Heart hidden when the signed-in viewer has an active Following on the project — keeps
 *       Saved / Doing visibly distinct (see UI_PLAN §2 / §4).
 * </ul>
 *
 * The favorite-stats query runs in parallel with the project hydrate query (see PHASE10_STATUS.md
 * for the modulith-cycle rationale that pulled these fields off ProjectDetail).
 */
export const ProjectDetailFavoriteSlot = ({ projectId }: ProjectDetailFavoriteSlotProps) => {
  const auth = useAuth()
  const stats = useFavoriteStats(projectId)
  const followings = useMyFollowings()

  const count = stats.data?.favoriteCount ?? null
  const isFavoritedByMe = stats.data?.isFavoritedByMe ?? false
  const isFollowedByMe = followings.data?.some((f) => f.projectId === projectId) ?? false

  // Anonymous: count only, never the toggle.
  if (!auth.isAuthenticated) {
    return <FavoriteCountOnly count={count} />
  }
  // Doer-overlap: count only — Doing tab is the right place for these projects.
  if (isFollowedByMe) {
    return <FavoriteCountOnly count={count} />
  }
  // Signed-in viewer who is not Doing — interactive toggle.
  return (
    <FavoriteHeartButton
      projectId={projectId}
      isFavoritedByMe={isFavoritedByMe}
      favoriteCount={count}
    />
  )
}

const FavoriteCountOnly = ({ count }: { count: number | null }) => (
  <div
    className="flex h-9 items-center gap-1.5 px-2 text-sm text-muted-foreground"
    aria-label={count == null ? 'Favorite count' : `${count} favorites`}
  >
    <Heart className="h-4 w-4" aria-hidden />
    <span className="tabular-nums">{count ?? '—'}</span>
  </div>
)
